<template>
  <ConfirmDialog data-test-id="confirm-dialog" />
  <Toast position="top-right" :showCloseIcon="true" class="p-toast-detail">
    <template #message="{ message }">
      <div
        class="flex flex-row justify-between w-full"
        :data-test-id="message.data?.['data-test-id']"
      >
        <div class="flex flex-col break-words">
          <span class="font-medium text-base">{{ message.summary }}</span>
          <span
            v-if="message.detail"
            class="text-sm text-surface-900 dark:text-surface-100 whitespace-pre-line"
            >{{ message.detail }}</span
          >
          <Button
            v-if="message.data?.hasDetails && message.data?.apiError"
            label="More Details"
            outlined
            class="mt-2"
            size="small"
            @click="showErrorDetails(message.data.apiError, message.summary)"
            data-test-id="error-more-details-link"
          />
        </div>
      </div>
    </template>
  </Toast>

  <!-- Global Error Details Drawer -->
  <ErrorDetailsDrawer />

  <!-- Session Expiration Dialog -->
  <Dialog
    v-model:visible="showSessionExpiredDialog"
    header="Your session has expired"
    :modal="true"
    :closable="false"
    :style="{ width: '450px' }"
    :draggable="false"
    data-test-id="session-expired-dialog"
  >
    <div class="flex items-start space-x-3">
      <div class="flex-1">
        <p class="text-surface-600 dark:text-surface-300 text-sm">
          For your security, you have been logged out due to inactivity. Please
          log in again to continue.
        </p>
      </div>
    </div>

    <template #footer>
      <Button
        label="Log In"
        icon="pi pi-sign-in"
        @click="logout"
        severity="primary"
        data-test-id="session-expired-login-btn"
      />
    </template>
  </Dialog>

  <!-- Login Recovery Dialog — OAuth token-exchange failure (APPINT-2811) -->
  <Dialog
    v-model:visible="showLoginRecoveryDialog"
    header="Login didn't complete"
    :modal="true"
    :closable="false"
    :style="{ width: '450px' }"
    :draggable="false"
    data-test-id="login-recovery-dialog"
  >
    <div class="flex items-start space-x-3">
      <div class="flex-1">
        <p class="text-surface-600 dark:text-surface-300 text-sm">
          Something went wrong while signing you in, and saved login data in
          this browser may be blocking another attempt. Clear it and log in
          again to continue.
        </p>
      </div>
    </div>

    <template #footer>
      <Button
        label="Clear session and log in again"
        icon="pi pi-refresh"
        :loading="isRecoveringLogin"
        :disabled="isRecoveringLogin"
        @click="recoverLogin"
        severity="primary"
        data-test-id="login-recovery-btn"
      />
    </template>
  </Dialog>

  <!-- Global Search Dialog -->
  <TaxTransactionSearchDialog />
  <div
    v-if="feLoaded && !redirectTarget"
    class="w-full h-dvh max-h-dvh flex flex-col items-stretch overflow-y-hidden"
  >
    <top-bar :logout="logout"></top-bar>
    <div class="w-full h-screen flex items-stretch pt-14">
      <side-bar />

      <main
        class="max-h-full w-full overflow-y-auto text-surface-900 dark:text-surface-0"
      >
        <div class="px-4 w-full relative">
          <router-view v-slot="{ Component }">
            <transition name="fade" mode="out-in">
              <component :is="Component" />
            </transition>
          </router-view>
        </div>
      </main>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useFrontegg, ContextHolder, useFronteggAuthGuard } from '@frontegg/vue'
import { RouterView } from 'vue-router'
import TopBar from '@/components/TopBar.vue'
import SideBar from '@/components/sidebar/SideBar.vue'

import Toast from 'primevue/toast'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import TaxTransactionSearchDialog from '@/components/taxtransaction/TaxTransactionSearchDialog.vue'
import ErrorDetailsDrawer from '@/components/common/ErrorDetailsDrawer.vue'
import { computed, ref, watch } from 'vue'
import { usePageMeta } from '@/composables/usePageMeta'
import { useErrorDetailsStore } from '@/stores/errorDetailsStore'
import { ApiError } from '@/util/ApiError'

const errorDetailsStore = useErrorDetailsStore()

// Show error details in the drawer
function showErrorDetails(error: unknown, title?: string) {
  if (ApiError.isApiError(error)) {
    errorDetailsStore.showErrorDetails(error, title)
  }
}
import {
  useSessionExpiration,
  createTestAuthHelpers,
} from '@/composables/useSessionExpiration'
import { useDatadogRum } from '@/composables/useDatadogRum'
import { clearFronteggClientState } from '@/util/fronteggSession'
import {
  partnerSupplierRedirectTarget,
  permissionKeysOf,
} from '@/util/partnerSupplierRedirect'
import { CONFIG } from '@/config/index'

const { fronteggLoaded: feLoaded, authState } = useFrontegg()
const sessionExpirationState = useSessionExpiration(authState, feLoaded)
const { showSessionExpiredDialog, showLoginRecoveryDialog } =
  sessionExpirationState

// Initialize Datadog RUM user identification (skip in headless E2E runs)
const isHeadlessE2E =
  typeof navigator !== 'undefined' &&
  navigator.userAgent.includes('HeadlessChrome')
if (!isHeadlessE2E) {
  useDatadogRum()
}

// Dynamic page metadata based on route meta
usePageMeta()

// Apply auth guard with redirect URL from local storage if available
useFronteggAuthGuard({
  redirectUrl: window.localStorage.getItem('RCH_REDIRECT_URL') ?? undefined,
})

// Partner and supplier users with no Toolbox permission do not belong in
// Toolbox: suppliers go to the account app, partners to the Reach site.
// Send them as soon as their permissions load, on any URL. This runs before
// the route guard in main.js can show them an "Access Denied" toast, and
// the layout stays unrendered meanwhile, so Toolbox never shows them
// anything. `replace` keeps Toolbox out of their Back history.
const redirectTarget = computed(() =>
  partnerSupplierRedirectTarget(permissionKeysOf(authState.user), {
    accountAppUrl: CONFIG.ACCOUNT_APP_URL,
    reachSiteUrl: CONFIG.REACH_SITE_URL,
  }),
)
watch(
  redirectTarget,
  target => {
    if (target) window.location.replace(target)
  },
  { immediate: true },
)

// Store the current URL for redirect after login if not already authenticated
watch(
  () => [authState.isAuthenticated, authState.isLoading],
  ([isAuthenticated, isLoading]) => {
    if (!isAuthenticated && !isLoading) {
      const redirectUrl = window.location.pathname + window.location.search
      if (!redirectUrl.includes('oauth')) {
        window.localStorage.setItem('RCH_REDIRECT_URL', redirectUrl)
      }
    }
  },
)

function logout() {
  const baseUrl = ContextHolder.getContext().baseUrl
  window.location.href = `${baseUrl}/oauth/logout?post_logout_redirect_uri=${window.location}`
}

// OAuth token-exchange failure recovery: a 431 header-bloat
// state re-sends itself on every login attempt, so the client state must be
// cleared before redirecting to /oauth/logout for a fresh authorize.
const isRecoveringLogin = ref(false)
function recoverLogin() {
  const wasAlreadyProcessing = isRecoveringLogin.value
  isRecoveringLogin.value = true
  if (wasAlreadyProcessing) return
  // no finally-reset: logout() navigates away, the spinner should hold
  clearFronteggClientState()
  logout()
}

// Expose auth state and test helpers for testing purposes
// @ts-ignore - Only used in test environment
if (import.meta.env.MODE !== 'prod') {
  // @ts-ignore
  window.__TEST_AUTH_HELPERS__ = createTestAuthHelpers(
    authState,
    feLoaded,
    sessionExpirationState,
  )
}
</script>

<style>
.p-toast-detail {
  white-space: pre;
}
</style>
